import { BlackjackEvents } from '../core/events/blackjack.js';
import {
  validateBlackjackBetConfirmation,
  validateBlackjackGameOver,
  validateBlackjackHandAction,
  validateBlackjackHandsUpdate,
  validateBlackjackInsuranceAction,
  validateBlackjackSplitGameOver,
  validateBlackjackSplitHandsUpdate,
} from '../core/validation/blackjack.js';

import { BaseSDKService } from './BaseSDKService.js';

/**
 * Blackjack SDK service — drop-in replacement for `BlackjackAIService` (iframe lib).
 *
 * Mirrors the iframe class method-for-method so studios switch modes with a
 * factory:
 *
 *     const Service = mode === 'iframe' ? BlackjackAIService : BlackjackSDKService;
 *     const client = new Service(config);
 *
 * `SDKServiceConfig` lacks `containerId` (iframe-only) but adds `serviceUrl`
 * as required. SDK-only additions (`startAudio`, `getAudioAnalyser`,
 * `getVideoTrack`, `getState`, PTT primitives) are inherited from
 * `BaseSDKService`.
 */
export class BlackjackSDKService extends BaseSDKService {
  sendWelcome(message?: string): void {
    this.dispatch(BlackjackEvents.createWelcome(message));
  }

  sendPlaceBet(minBet?: number, maxBet?: number): void {
    this.dispatch(BlackjackEvents.createPlaceBet(minBet, maxBet));
  }

  sendBetConfirmation(bet: number, roundId: string): void {
    validateBlackjackBetConfirmation(bet, roundId);
    this.dispatch(BlackjackEvents.createBetConfirmation(bet, roundId.trim()));
  }

  sendHandAction(action: BlackjackEvents.PlayerMove): void {
    validateBlackjackHandAction(action);
    this.dispatch(BlackjackEvents.createHandAction(action));
  }

  sendHandsUpdate(
    dealerHand: string[],
    playerHandCards: string[],
    playerHandStatus: BlackjackEvents.IntermediateStatus,
    playerHandTotal: number,
    dealerHandTotal?: number,
    roundId?: string,
    insuranceAvailable?: boolean,
    insuranceCost?: number,
  ): void {
    validateBlackjackHandsUpdate(dealerHand, playerHandCards, playerHandTotal, insuranceCost);

    const data: Record<string, unknown> = {
      dealerHand,
      playerHandCards,
      playerHandStatus,
      playerHandTotal,
    };
    if (dealerHandTotal !== undefined) data.dealerHandTotal = dealerHandTotal;
    if (roundId) data.roundId = roundId;
    if (insuranceAvailable !== undefined) data.insuranceAvailable = insuranceAvailable;
    if (insuranceCost !== undefined) data.insuranceCost = insuranceCost;

    this.dispatchRaw(BlackjackEvents.Type.HANDS_UPDATE, data);
  }

  sendSplitHandsUpdate(
    dealerHand: string[],
    playerHands: BlackjackEvents.PlayerHand[],
    activeHand: 0 | 1,
    dealerHandTotal?: number,
    roundId?: string,
    dealerOpenCard?: string,
    insuranceAvailable?: boolean,
    insuranceCost?: number,
  ): void {
    validateBlackjackSplitHandsUpdate({
      dealerHand,
      playerHands,
      activeHand,
      dealerHandTotal,
      insuranceCost,
    });

    this.dispatchRaw(BlackjackEvents.Type.SPLIT_HANDS_UPDATE, {
      dealerHand,
      ...(dealerHandTotal !== undefined ? { dealerHandTotal } : {}),
      playerHands,
      activeHand,
      ...(dealerOpenCard ? { dealerOpenCard } : {}),
      ...(roundId ? { roundId } : {}),
      ...(insuranceAvailable !== undefined ? { insuranceAvailable } : {}),
      ...(insuranceCost !== undefined ? { insuranceCost } : {}),
    });
  }

  sendInsuranceAction(taken: boolean, amount?: number, roundId?: string): void {
    validateBlackjackInsuranceAction(taken, amount, roundId);
    this.dispatch(BlackjackEvents.createInsuranceAction(taken, amount, roundId));
  }

  sendGameOver(
    dealerHand: string[],
    dealerHandTotal: number,
    playerHandCards: string[],
    playerHandStatus: BlackjackEvents.GameOutcome,
    playerHandTotal: number,
    win: number,
    roundId: string,
    insuranceTaken?: boolean,
    insuranceWin?: number,
  ): void {
    validateBlackjackGameOver({
      dealerHand,
      dealerHandTotal,
      playerHandCards,
      playerHandStatus,
      playerHandTotal,
      win,
      roundId,
      insuranceTaken,
      insuranceWin,
    });

    this.dispatchRaw(BlackjackEvents.Type.GAME_OVER, {
      dealerHand,
      dealerHandTotal,
      playerHandCards,
      playerHandStatus,
      playerHandTotal,
      win,
      roundId: roundId.trim(),
      ...(insuranceTaken !== undefined ? { insuranceTaken } : {}),
      ...(insuranceWin !== undefined ? { insuranceWin } : {}),
    });
  }

  sendSplitGameOver(
    dealerHand: string[],
    dealerHandTotal: number,
    playerHands: BlackjackEvents.PlayerHand[],
    playerHandStatus: BlackjackEvents.RoundOutcome,
    win: number,
    roundId: string,
    insuranceTaken?: boolean,
    insuranceWin?: number,
  ): void {
    validateBlackjackSplitGameOver({
      dealerHand,
      dealerHandTotal,
      playerHands,
      playerHandStatus,
      win,
      roundId,
      insuranceTaken,
      insuranceWin,
    });

    this.dispatchRaw(BlackjackEvents.Type.SPLIT_GAME_OVER, {
      dealerHand,
      dealerHandTotal,
      playerHands,
      playerHandStatus,
      win,
      roundId: roundId.trim(),
      ...(insuranceTaken !== undefined ? { insuranceTaken } : {}),
      ...(insuranceWin !== undefined ? { insuranceWin } : {}),
    });
  }

  sendBetTimerExpired(): void {
    this.dispatch(BlackjackEvents.createBetTimerExpired());
  }

  hit(): void {
    this.sendHandAction('hit');
  }

  stand(): void {
    this.sendHandAction('stand');
  }

  double(): void {
    this.sendHandAction('double');
  }

  split(): void {
    this.sendHandAction('split');
  }

  private dispatchRaw(type: string, data: unknown): void {
    this.dispatch({ type, data });
  }
}
